import { logger, type LogMeta } from '@/utils/logger';
import {
  getScheduleDiagnostics,
  type ScheduleDiagnostics,
  type ScheduleSource,
} from '@/utils/scheduling';

interface ScheduleLogPlan extends ScheduleSource {
  id?: string;
  ID?: string;
  planId?: string;
  vendorId?: string;
  vendorName?: string;
  name?: string;
}

interface ScheduleLogSummary {
  serviceName: string;
  stage: string;
  zipCode?: string;
  planId?: string;
  vendorId?: string;
  vendorName?: string;
  planName?: string;
  diagnostics: ScheduleDiagnostics;
}

interface LogPlanScheduleDiagnosticsArgs {
  area?: string;
  serviceName: string;
  stage: string;
  zipCode?: string;
  userId?: string;
  plans: ScheduleLogPlan[];
}

interface LogSinglePlanScheduleArgs {
  area?: string;
  serviceName: string;
  stage: string;
  zipCode?: string;
  userId?: string;
  plan: ScheduleLogPlan | null | undefined;
  extra?: LogMeta;
}

const MAX_SAMPLE_PLANS = 5;

export function getPlanScheduleLogSummary(
  plan: ScheduleLogPlan,
  serviceName: string,
  stage: string,
  zipCode?: string
): ScheduleLogSummary {
  return {
    serviceName,
    stage,
    zipCode,
    planId: plan.planId || plan.id || plan.ID,
    vendorId: plan.vendorId,
    vendorName: plan.vendorName,
    planName: plan.name,
    diagnostics: getScheduleDiagnostics(plan),
  };
}

export function logPlanScheduleDiagnostics({
  area = 'plans',
  serviceName,
  stage,
  zipCode,
  userId,
  plans,
}: LogPlanScheduleDiagnosticsArgs): void {
  const summaries = plans.map((plan) => getPlanScheduleLogSummary(plan, serviceName, stage, zipCode));
  const missingWeekendPlans = summaries.filter(
    (summary) => summary.diagnostics.missingWeekendValues.length > 0
  );
  const noWorkDaySchedulePlans = summaries.filter(
    (summary) => !summary.diagnostics.hasAnyWorkDaySchedule
  );
  const weekendAllowedPlans = summaries.filter(
    (summary) =>
      summary.diagnostics.normalized.isSaturday === true ||
      summary.diagnostics.normalized.isSunday === true
  );

  logger.info(
    { area, userId },
    `${serviceName} schedule diagnostics (${stage})`,
    {
      serviceName,
      stage,
      zipCode,
      planCount: plans.length,
      missingWeekendScheduleCount: missingWeekendPlans.length,
      noWorkDayScheduleCount: noWorkDaySchedulePlans.length,
      weekendAllowedCount: weekendAllowedPlans.length,
      sample: summaries.slice(0, MAX_SAMPLE_PLANS),
    }
  );

  if (missingWeekendPlans.length > 0) {
    logger.warn(
      { area, userId },
      `${serviceName} plan response missing isSaturday/isSunday schedule values (${stage})`,
      {
        serviceName,
        stage,
        zipCode,
        missingWeekendScheduleCount: missingWeekendPlans.length,
        sample: missingWeekendPlans.slice(0, MAX_SAMPLE_PLANS),
      }
    );
  }
}

export function logSinglePlanSchedule({
  area = 'plans',
  serviceName,
  stage,
  zipCode,
  userId,
  plan,
  extra,
}: LogSinglePlanScheduleArgs): void {
  if (!plan) {
    logger.warn(
      { area, userId },
      `${serviceName} schedule diagnostics missing plan (${stage})`,
      { serviceName, stage, zipCode, extra }
    );
    return;
  }

  const summary = getPlanScheduleLogSummary(plan, serviceName, stage, zipCode);
  logger.info(
    { area, userId },
    `${serviceName} selected schedule diagnostics (${stage})`,
    { ...summary, extra }
  );

  if (summary.diagnostics.missingWeekendValues.length > 0) {
    logger.warn(
      { area, userId },
      `${serviceName} selected plan missing isSaturday/isSunday schedule values (${stage})`,
      { ...summary, extra }
    );
  }
}
